import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProfessionalAdvisory = () => {
  const [selectedTab, setSelectedTab] = useState('commentary');

  const tabs = [
    { value: 'commentary', label: 'Expert Commentary', icon: 'MessageSquare' },
    { value: 'outlook', label: 'Weekly Outlook', icon: 'Calendar' },
    { value: 'recommendations', label: 'Investment Recommendations', icon: 'Target' }
  ];

  const expertCommentary = [
    {
      id: 1,
      expert: "Dr. Sarah Chen",
      title: "Senior Crypto Analyst",
      company: "BlockChain Capital",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      timestamp: "3 hours ago",
      content: `The current market consolidation phase presents an excellent opportunity for strategic positioning. Bitcoin's recent stability above $65,000 indicates strong institutional support, while Ethereum's network improvements continue to drive fundamental value.\n\nKey observations:\n• Institutional inflows remain robust despite short-term volatility\n• DeFi sector showing signs of recovery with improved yields\n• Regulatory clarity in major markets creating positive sentiment\n\nRecommendation: Consider dollar-cost averaging into major assets during this consolidation period.`,
      sentiment: "bullish",
      accuracy: "94%"
    },
    {
      id: 2,
      expert: "Michael Rodriguez",
      title: "Head of Research",
      company: "Digital Asset Management",
      avatar: "https://randomuser.me/api/portraits/men/45.jpg",
      timestamp: "6 hours ago",
      content: `Market technicals suggest we're approaching a critical decision point. The 200-day moving average confluence with key resistance levels will likely determine the next major move.\n\nTechnical Analysis:\n• RSI showing bullish divergence on daily charts\n• Volume profile indicates accumulation at current levels\n• Options flow suggests institutional hedging for upside moves\n\nCaution: Monitor macro economic indicators closely as they continue to influence crypto correlations.`,
      sentiment: "neutral",
      accuracy: "91%"
    }
  ];

  const weeklyOutlook = {
    period: "December 9-15, 2024",
    overallSentiment: "Cautiously Optimistic",
    keyEvents: [
      { date: "Dec 10", event: "Federal Reserve Interest Rate Decision", impact: "High" },
      { date: "Dec 12", event: "Ethereum Network Upgrade", impact: "Medium" },
      { date: "Dec 14", event: "Options Expiry ($2.1B)", impact: "High" }
    ],
    predictions: [
      { asset: "Bitcoin", target: "$68,000-$72,000", probability: "65%", timeframe: "7 days" },
      { asset: "Ethereum", target: "$4,200-$4,600", probability: "70%", timeframe: "7 days" },
      { asset: "Market Cap", target: "$2.6T-$2.8T", probability: "60%", timeframe: "7 days" }
    ],
    risks: [
      "Federal Reserve hawkish stance could pressure risk assets",
      "Year-end profit taking may increase volatility",
      "Regulatory developments in major markets"
    ]
  };

  const recommendations = [
    {
      id: 1,
      type: "Buy",
      asset: "Bitcoin (BTC)",
      allocation: "40%",
      targetPrice: "$70,000",
      timeHorizon: "3-6 months",
      riskLevel: "Medium",
      rationale: "Strong institutional adoption, improving regulatory environment, and technical breakout potential make Bitcoin attractive for medium-term holdings.",
      stopLoss: "$62,000",
      confidence: "High"
    },
    {
      id: 2,
      type: "Accumulate",
      asset: "Ethereum (ETH)",
      allocation: "30%",
      targetPrice: "$4,800",
      timeHorizon: "6-12 months",
      riskLevel: "Medium",
      rationale: "Network improvements, growing DeFi ecosystem, and upcoming developments position Ethereum for continued growth.",
      stopLoss: "$3,800",
      confidence: "High"
    },
    {
      id: 3,
      type: "Speculative Buy",
      asset: "Solana (SOL)",
      allocation: "15%",
      targetPrice: "$280",
      timeHorizon: "3-9 months",
      riskLevel: "High",
      rationale: "Strong ecosystem growth, improving network stability, and institutional interest support higher valuations.",
      stopLoss: "$180",
      confidence: "Medium"
    },
    {
      id: 4,
      type: "Hold",
      asset: "Cardano (ADA)",
      allocation: "10%",
      targetPrice: "$0.85",
      timeHorizon: "6-12 months",
      riskLevel: "Medium",
      rationale: "Steady development progress and growing ecosystem, but facing increased competition from other smart contract platforms.",
      stopLoss: "$0.45",
      confidence: "Medium"
    }
  ];

  const getTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'buy': return 'text-success bg-success/10';
      case 'accumulate': return 'text-accent bg-accent/10';
      case 'speculative buy': return 'text-warning bg-warning/10';
      case 'hold': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'low': return 'text-success';
      case 'medium': return 'text-accent';
      case 'high': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const renderCommentary = () => (
    <div className="space-y-6">
      {expertCommentary?.map((comment) => (
        <div key={comment?.id} className="border border-border rounded-lg p-6">
          <div className="flex items-start space-x-4 mb-4">
            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
              <Image src={comment?.avatar} alt={comment?.expert} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <h4 className="font-semibold text-card-foreground">{comment?.expert}</h4>
                <span className="text-xs text-muted-foreground">{comment?.timestamp}</span>
              </div>
              <p className="text-sm text-muted-foreground mb-1">{comment?.title}</p>
              <p className="text-xs text-muted-foreground">{comment?.company}</p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-success font-medium">Accuracy: {comment?.accuracy}</span>
              <div className={`w-2 h-2 rounded-full ${comment?.sentiment === 'bullish' ? 'bg-success' : comment?.sentiment === 'bearish' ? 'bg-error' : 'bg-accent'}`}></div>
            </div>
          </div>
          <div className="prose prose-sm max-w-none">
            <p className="text-sm text-card-foreground whitespace-pre-line leading-relaxed">
              {comment?.content}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const renderOutlook = () => (
    <div className="space-y-6">
      <div className="border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-card-foreground">Weekly Market Outlook</h4>
          <span className="text-sm text-muted-foreground">{weeklyOutlook?.period}</span>
        </div>
        <div className="mb-6">
          <span className="text-sm text-muted-foreground">Overall Sentiment: </span>
          <span className="text-sm font-medium text-accent">{weeklyOutlook?.overallSentiment}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-card-foreground mb-3">Key Events</h5>
            <div className="space-y-2">
              {weeklyOutlook?.keyEvents?.map((event, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-card-foreground">{event?.event}</p>
                    <p className="text-xs text-muted-foreground">{event?.date}</p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded ${
                    event?.impact === 'High' ? 'bg-error/10 text-error' : 'bg-accent/10 text-accent'
                  }`}>
                    {event?.impact}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h5 className="font-medium text-card-foreground mb-3">Price Predictions</h5>
            <div className="space-y-2">
              {weeklyOutlook?.predictions?.map((prediction, index) => (
                <div key={index} className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-card-foreground">{prediction?.asset}</span>
                    <span className="text-xs text-success font-medium">{prediction?.probability}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{prediction?.target}</span>
                    <span className="text-xs text-muted-foreground">{prediction?.timeframe}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <h5 className="font-medium text-card-foreground mb-3">Key Risks</h5>
          <ul className="space-y-2">
            {weeklyOutlook?.risks?.map((risk, index) => (
              <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  const renderRecommendations = () => (
    <div className="space-y-4">
      {recommendations?.map((rec) => (
        <div key={rec?.id} className="border border-border rounded-lg p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getTypeColor(rec?.type)}`}>
                {rec?.type}
              </span>
              <h4 className="text-lg font-semibold text-card-foreground">{rec?.asset}</h4>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Confidence</p>
              <p className="text-sm font-medium text-card-foreground">{rec?.confidence}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Allocation</p>
              <p className="text-sm font-medium text-card-foreground">{rec?.allocation}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Target Price</p>
              <p className="text-sm font-medium text-card-foreground">{rec?.targetPrice}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Time Horizon</p>
              <p className="text-sm font-medium text-card-foreground">{rec?.timeHorizon}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Risk Level</p>
              <p className={`text-sm font-medium ${getRiskColor(rec?.riskLevel)}`}>{rec?.riskLevel}</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-3">{rec?.rationale}</p>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Stop Loss: <span className="text-error font-medium">{rec?.stopLoss}</span></span>
            <span className="flex items-center space-x-1">
              <Icon name="Shield" size={12} />
              <span>Risk-adjusted recommendation</span>
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground mb-2">Professional Advisory</h2>
          <p className="text-sm text-muted-foreground">
            Expert market analysis and investment recommendations from certified professionals
          </p>
        </div>
        <div className="flex items-center space-x-2 text-xs text-muted-foreground">
          <Icon name="Shield" size={16} className="text-success" />
          <span>Verified Experts</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6 border-b border-border">
        {tabs?.map((tab) => (
          <button
            key={tab?.value}
            onClick={() => setSelectedTab(tab?.value)}
            className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-standard ${
              selectedTab === tab?.value
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name={tab?.icon} size={16} />
            <span>{tab?.label}</span>
          </button>
        ))}
      </div>
      <div>
        {selectedTab === 'commentary' && renderCommentary()}
        {selectedTab === 'outlook' && renderOutlook()}
        {selectedTab === 'recommendations' && renderRecommendations()}
      </div>
    </div>
  );
};

export default ProfessionalAdvisory;